"use client";

/**
 * Providers v2 - Auth Worker Version
 *
 * Uses the new Auth Worker for authentication instead of Supabase Auth.
 * To switch to this version, rename this file to providers.tsx
 */

import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GatewayProvider } from "@/lib/contexts/GatewayContext";
import { LanguageProvider } from "@/lib/contexts/LanguageContext";
import { HtmlLangUpdater } from "@/lib/contexts/HtmlLangUpdater";
import { BrandThemeProvider } from "@/components/providers/BrandThemeProvider";
import { AuthProvider, useAuth } from "@/lib/auth";

// Create a client outside component to prevent recreation on re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Bridge component that connects AuthProvider to GatewayProvider
 * Maintains backwards compatibility with existing components using useGateway()
 */
function AuthGatewayBridge({ children }: { children: React.ReactNode }) {
  const { user, loading, signOut, isAdmin, isTeamMember, isClient } = useAuth();

  // Map auth user to the User type expected by GatewayProvider
  const gatewayUser = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        avatar_url: user.avatar_url ?? undefined,
        role: user.role,
        company_id: user.company_id ?? undefined,
        is_active: user.is_active,
      }
    : null;

  return (
    <GatewayProvider
      initialUser={gatewayUser}
      initialLoading={loading}
      onSignOut={signOut}
    >
      {children}
    </GatewayProvider>
  );
}

export function Providers({ children }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider defaultLanguage="es">
        <HtmlLangUpdater />
        <BrandThemeProvider>
          <AuthProvider>
            <AuthGatewayBridge>{children}</AuthGatewayBridge>
          </AuthProvider>
        </BrandThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
